import { defineComponent as _defineComponent } from 'vue'
import DomService from '@/core/dom.service';
import PageService from '@/core/page.service';
import { store } from '@/store';
import { QueryParams } from '@/core/query-params.service';
import { VueAutosuggest } from 'vue-autosuggest';
import Http from '@/core/http/http.service';
import SearchSuggestViewObject = Vertica.LouisPoulsen.Application.Search.ViewModels.SearchSuggestViewObject;
import SearchSuggestResultsViewModel = Vertica.LouisPoulsen.Application.Search.ViewModels.SearchSuggestResultsViewModel;
import { Dictionary } from 'vue-router/types/router';
import SearchTrackingService from './SearchTracking.service';
import { debounce } from 'lodash';
import viewportEventsService from '@/core/responsive/viewport/viewport-events.service';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { watchRelativeUrl } from '@/core/route.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchField',
  props: {
    autosuggest: { type: Boolean, default: true },
    placeholder: { default: '' },
    inputClass: { default: '' },
    suggestClass: { default: '' },
    onSearchPage: { type: Boolean, default: false }
  },
  emits: ["on-search", "togglePopout", "searchPageSearchActive"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'on-search', term: string) : void,
    (e: 'togglePopout', value: boolean) : void,
    (e: 'searchPageSearchActive', active: boolean, y?: number, height?: number) : void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { autosuggest: boolean, placeholder: string, inputClass: string, suggestClass: string, onSearchPage: boolean }





const showAuthTopBar = computed<boolean>(() => store.getters['app/showAuthTopBar']);
const showEmptyTopBar = computed<boolean>(() => store.getters['app/showEmptyTopBar']);
const searchResultUrl = computed<string>(() => store.getters['app/searchResultUrl']);

function toggleSearch(canOpenSearch?: boolean) {
    store.dispatch('navigation/toggleSearch', canOpenSearch);
}

const term = ref<string>('');
const suggestions = ref<SearchSuggestViewObject[]>([]);
const opened = ref<boolean>(false);
const suggestSequence = ref<number>(0);
const id = Math.floor(Math.random() * 1000000).toString();

const debounceFetchSuggestions: Function = debounce<any>(fetchSuggestions, 50);

const inputProps = computed<object>(() => {
    return {
        id: 'site_search_field_id' + id,
        name: 'site_search_field' + id,
        ref: 'siteSearchField',
        placeholder: props.placeholder,
        type: 'text',
        autocomplete: 'off',
        autocapitalize: 'none',
        class: 'search-field__input c-search-input u-pl-10 u-pr-6 lg:u-pb-2 hover:u-border-black focus:u-border-black ' + props.inputClass
    };
});

const siteSearchFieldSuggest = ref<any>(null);

const router = useRouter();
const isSearchPage = computed<boolean>(() => {
    return router.currentRoute.path === searchResultUrl.value;
});

watchRelativeUrl(onRouteChange);
function onRouteChange(route, oldRoute): void {
    if (route && oldRoute && route.path === oldRoute.path) {
        setTermFromQuery();
    }
}

onMounted(setTermFromQuery);

function closeSearch() {
    closePopout();
    toggleSearch(!isSearchPage.value);
    if (isSearchPage.value) {
        term.value = '';
    }
}

async function fetchSuggestions(term): Promise<void> {
    if (term && props.autosuggest) {
        const requestSeq = ++suggestSequence.value;
        const response = await Http.get<SearchSuggestResultsViewModel>('/search/searchsuggest', { term });
        if (requestSeq === suggestSequence.value) {
            suggestions.value = response.suggestions;
        }
    } else {
        suggestions.value.length = 0;
    }
    SearchTrackingService.trackSearchInput(term);
}

function onSuggestionSelected(selected: { item: SearchSuggestViewObject }): void {
    if (selected) {
        if (selected.item.url) {
            PageService.softRedirect(selected.item.url);
        } else {
            term.value = selected.item.name;
            SearchTrackingService.setSelectedSearchSuggest(selected.item.name);
            search();
        }
        closeSearch();
    }
}

function search(): void {
    const query: Dictionary<string> = { [QueryParams.SearchTerm]: term.value };
    const path: string = searchResultUrl.value;
    const action = isSearchPage.value ? 'replace' : 'push';

    router[action]({ path, query });
    emit('on-search', term.value);
    suggestions.value.length = 0;
    blur(); // Perhaps only for touchbased input - to hide the keypad.

    SearchTrackingService.trackSelectedSearch(term.value);
}

function togglePopout(value: boolean) {
    if (opened.value === value) {
        return;
    }
    if (!isSearchPage.value) {
        emit('togglePopout', value);
    }
    setTimeout(() => {
        opened.value = value;
    }, 10);
}

function openPopout() {
    onSearchOpen();
    togglePopout(true);
    if (isSearchPage.value) {
        setTimeout(() => {
            const results = document.getElementById('autosuggest-autosuggest__results').getBoundingClientRect() as DOMRect;
            emit('searchPageSearchActive', true, results.y + viewportEventsService.viewport.scrollY, results.height);
        }, 10);
    }
}

function closePopout() {
    onSearchClose();
    togglePopout(false);
    if (isSearchPage.value) {
        emit('searchPageSearchActive', false);
    }
}

const popoutContainerClass = computed<string>(() => {
    let cssClass = props.onSearchPage ? 'search-page-popout-container o-new-grid' : 'search-popout-container o-new-grid';
    if (opened.value) cssClass += ' -open';
    if (showAuthTopBar.value) cssClass += ' -show-auth-top-bar';
    if (showEmptyTopBar.value) cssClass += ' -empty-top-bar';
    return cssClass;
});

const popoutClass = computed<string>(() => {
    let cssClass = 'search-popout';
    if (opened.value) cssClass += ' -open';
    return cssClass;
});

function setTermFromQuery(): void {
    term.value = router.currentRoute.query[QueryParams.SearchTerm] as string;
}

// Method for 'disabling' form tab arrows on iOS keyboard
function focus(): void {
    setTimeout(() => {
        const suggest = siteSearchFieldSuggest.value as Vue;
        (suggest.$refs.siteSearchField as HTMLElement).focus();
    }, 100);
}

function blur(): void {
    const suggest = siteSearchFieldSuggest.value as Vue;
    (suggest.$refs.siteSearchField as HTMLElement).blur();
}

function onSearchOpen() {
    DomService.addElementClass('html', 'search-open');
}

function onSearchClose() {
    DomService.removeElementClass('html', 'search-open');
}

expose({ focus, closePopout });

return { __sfc: true,props, emit, showAuthTopBar, showEmptyTopBar, searchResultUrl, toggleSearch, term, suggestions, opened, suggestSequence, id, debounceFetchSuggestions, inputProps, siteSearchFieldSuggest, router, isSearchPage, onRouteChange, closeSearch, fetchSuggestions, onSuggestionSelected, search, togglePopout, openPopout, closePopout, popoutContainerClass, popoutClass, setTermFromQuery, focus, blur, onSearchOpen, onSearchClose, VueAutosuggest }
}

})